import { useCallback } from 'react';
import { LAYOUT_CONFIG, getLayoutCategory, calculateNodeHeight, NODE_HEIGHTS, PADDING_Y } from '../layoutConfig';
import { getNodeConfig, CHART_VISUAL_TYPES, GRAPH_EDGE_STYLES } from '../config/nodeVisualConfig';
import { findAllAssociatedEntryNodes } from '../utils/nodeUtils';

// Consolidated Node Factory Hook
// Unifies logic between Toolbox Click (StrategyBuilder) and Drag & Drop (StrategyLogicStep)
export const useNodeFactory = (nodes, setNodes, setEdges) => {

  const createNode = useCallback((type, positionArg, dataArg, options = {}) => {
      const isDataSource = type === 'data_source';
      const isChartVisual = CHART_VISUAL_TYPES.includes(type);
      
      const itemLabel = (t, d) => d?.label || d?.name || t;
      const label = itemLabel(type, dataArg);

      // 1. Singleton Check: Data Source (Allow if explicitly creating Secondary)
      if (isDataSource && !dataArg?.isSecondary) {
          const existingSource = nodes.find(n => n.type === 'data_source');
          if (existingSource) {
              return { success: false, message: 'Data Source already exists', node: existingSource, createdEdges: [] };
          }
      }

      // 2. Singleton Check: Visual Types
      if (isChartVisual) {
          const existingVisual = nodes.find(n => n.type === type);
          if (existingVisual) {
              return { success: false, message: `${label} already on chart`, node: existingVisual, createdEdges: [] };
          }
      }

      // 3. Singleton Check: Main Chart Output
      if (type === 'chart_output' && dataArg?.panel === 'main') {
          const existingMainChart = nodes.find(n => n.type === 'chart_output' && n.data?.panel === 'main');
          if (existingMainChart) {
               return { success: false, message: 'Main Chart Output already exists', node: existingMainChart, createdEdges: [] };
          }
      }

      // 3.5. Singleton Check: Execution Signals (Multi-stage Support)
      // autoUpgraded is set to true when an existing single-stage node is silently promoted
      // to multi_stage rather than blocking creation. Callers receive this flag in the
      // result and can show an informative toast instead of an error.
      let autoUpgraded = false;
      let autoUpgradedLabels = [];
      if (type === 'signal' || type === 'position_trigger') {
          const actionType = dataArg?.action;
          const positionType = (dataArg?.type || '').toUpperCase();
          const isRiskLong = positionType === 'LONG' || positionType === 'LONG RISK';
          const isRiskShort = positionType === 'SHORT' || positionType === 'SHORT RISK';

          if (actionType) {
              // Find exiting execution nodes of the exact same action type AND position type
              const existingNodes = nodes.filter(n => {
                  if (n.type !== 'signal' && n.type !== 'position_trigger') return false;
                  if (n.data?.action !== actionType) return false;
                  
                  // For SL/TP, additionally match LONG vs SHORT
                  if (['sl', 'tp'].includes(actionType)) {
                      const nType = (n.data?.type || '').toUpperCase();
                      const nIsRiskLong = nType === 'LONG' || nType === 'LONG RISK';
                      const nIsRiskShort = nType === 'SHORT' || nType === 'SHORT RISK';
                      if (isRiskLong && !nIsRiskLong) return false;
                      if (isRiskShort && !nIsRiskShort) return false;
                  }
                  
                  return true;
              });

              if (existingNodes.length > 0) {
                  // SL/TP nodes can be multi-stage (scale-out) independently of entries.
                  // Entry nodes (enter_long/enter_short) still require explicit opt-in.

                  // If any existing node is NOT multi-stage, block creation.
                  // For exits: we apply the same discipline — user must upgrade
                  // existing exit node to multi-stage before adding a second one.
                  const hasSingleExecutionNode = existingNodes.some(n => 
                      !n.data?.execution_strategy || n.data?.execution_strategy === 'single'
                  );

                  if (hasSingleExecutionNode) {
                      // AUTO-UPGRADE: silently promote every single-stage node of the same
                      // action/direction to multi_stage instead of blocking with an error.
                      // The user gets a friendly toast via the autoUpgraded flag on the result.
                      const toUpgrade = existingNodes.filter(n =>
                          !n.data?.execution_strategy || n.data?.execution_strategy === 'single'
                      );
                      setNodes(nds => nds.map(n =>
                          toUpgrade.some(u => u.id === n.id)
                              ? { ...n, data: { ...n.data, execution_strategy: 'multi_stage' } }
                              : n
                      ));
                      autoUpgraded = true;
                      autoUpgradedLabels = toUpgrade.map(n => n.data?.label || n.data?.action || 'node');
                      // Fall through — the stage-assignment block below runs normally.
                  }
                  
                  // If we are allowed to add, auto-assign the next stage
                  const maxStage = Math.max(...existingNodes.map(n => n.data?.stage || 1));
                  dataArg.stage = maxStage + 1;
                  dataArg.execution_strategy = 'multi_stage';
              } else {
                  // First node defaults to single
                  dataArg.execution_strategy = dataArg?.execution_strategy || 'single';
                  dataArg.stage = 1;
              }
          }
      }

      // 3. Generate ID (Standardized)
      const id = `${type}_${Math.random().toString(36).substr(2, 9)}`;

      // 4. Determine Position (Layout Logic)
      let finalPosition = positionArg;
      
      if (!finalPosition) {
          // If no specific position provided (e.g. Click), calculate auto-layout
          const category = getLayoutCategory(type, dataArg);
          const config = LAYOUT_CONFIG[category] || LAYOUT_CONFIG['other'];
          const categoryNodes = nodes.filter(n => getLayoutCategory(n.type, n.data) === category);
          
          let finalY = config.startY;
          
          if (type === 'data_source') {
              const primarySource = nodes.find(n => n.type === 'data_source');
              if (primarySource) {
                  const sources = nodes.filter(n => n.type === 'data_source');
                  const lowermost = sources.reduce((prev, current) => (prev.position.y > current.position.y) ? prev : current, primarySource);
                  const pX = typeof lowermost.position?.x === 'number' ? lowermost.position.x : config.x;
                  const pY = typeof lowermost.position?.y === 'number' ? lowermost.position.y : config.startY;
                  const h = NODE_HEIGHTS[lowermost.type] || 120;
                  finalPosition = { x: pX, y: pY + h + PADDING_Y };
              } else {
                  finalPosition = { x: config.x, y: config.startY };
              }
          } else {
              // 1. Calculate Waterfall dependency (e.g. Visuals stay below Logic)
              if (category === 'visual') {
                  const logicCategories = ['indicator', 'math', 'comparator', 'logic_gate'];
                  const logicNodes = nodes.filter(n => logicCategories.includes(getLayoutCategory(n.type, n.data)));
                  const logicBottoms = logicNodes.map(n => {
                      const y = typeof n.position?.y === 'number' ? n.position.y : parseFloat(n.position?.y || 100);
                      const h = calculateNodeHeight(n);
                      return y + h;
                  });
                  const maxLogicBottom = logicNodes.length > 0 ? Math.max(...logicBottoms) : 0;
                  
                  // Start visual nodes below logic nodes, but at least at their own default startY
                  finalY = Math.max(config.startY, maxLogicBottom + 200); 
              }

              // 2. Adjust based on existing nodes in the same category
              if (categoryNodes.length > 0) {
                  const bottoms = categoryNodes.map(n => {
                      const y = typeof n.position?.y === 'number' ? n.position.y : parseFloat(n.position?.y || config.startY);
                      const h = calculateNodeHeight(n);
                      return isNaN(y) ? config.startY : (y + h);
                  });
                  const maxBottom = Math.max(...bottoms);
                  finalY = Math.max(finalY, maxBottom + (category === 'indicator' ? 6 : PADDING_Y));
              }

              finalPosition = { x: config.x, y: finalY };
          }
      }

      // 4. Default Data from Centralized Config
      const nodeConfig = getNodeConfig(type);
      const defaultNodeData = nodeConfig.defaultData || {};

      const newNode = {
          id,
          type,
          position: finalPosition,
          data: { 
              label, 
              ...defaultNodeData,
              ...dataArg,
              is_visible: (dataArg?.is_visible !== undefined) ? dataArg.is_visible : (dataArg?.initialVisible ?? false)
          }
      };

      // 5. Auto-Wiring Logic
      const edgesToAdd = [];
      const dataSourceNode = nodes.find(n => n.type === 'data_source');
      let mainChartNode = nodes.find(n => n.type === 'chart_output' && n.data?.panel === 'main');
      let subChartNode = nodes.find(n => n.type === 'chart_output' && n.data?.panel !== 'main');

      const connectToChart = (sourceId, targetChartId) => {
          edgesToAdd.push({
               id: `e_${sourceId}_${targetChartId}`,
               source: sourceId,
               target: targetChartId,
               animated: true,
               style: { ...GRAPH_EDGE_STYLES.DEFAULT }
          });
      };

      // A. Indicator Input Wiring (Data Source -> Indicator)
      if (type === 'indicator') {
           // If we have an explicit sourceId (e.g. from handleShowSlope), link to it
           const actualSourceId = newNode.data.sourceId || dataSourceNode?.id;
           
           if (actualSourceId) {
                newNode.data.sourceId = actualSourceId;
                const sourceNode = nodes.find(n => n.id === actualSourceId);
                const sourceTimeframe = sourceNode?.data?.timeframe || sourceNode?.data?.params?.timeframe;
                
                if (sourceTimeframe) {
                    if (!newNode.data.params) newNode.data.params = {};
                    newNode.data.timeframe = sourceTimeframe;
                    newNode.data.params.timeframe = sourceTimeframe;
                }

                edgesToAdd.push({
                    id: `e_${actualSourceId}_${newNode.id}`,
                    source: actualSourceId,
                    target: newNode.id,
                    ...(newNode.data.sourceHandle ? { sourceHandle: newNode.data.sourceHandle } : {}),
                    animated: true,
                    style: { ...GRAPH_EDGE_STYLES.DATA_TO_INDICATOR }
                });
           }
      }
      // B. Exit Node Auto-Wiring — connect signal handle (input_0) to the stage-matching
      //    entry node for ALL exit node types: sl, tp, exit_long, exit_short, time_exit.
      //
      // Design: Exit stage N is responsible for the position opened by Entry stage N.
      // The signal handle always shows WHICH entry this exit manages (association).
      // The trigger handle (trigger_input) is ONLY for CUSTOM comparator connections —
      // it is never managed here; the user wires it manually from the panel.
      //
      // SL/TP in CUSTOM mode still auto-wires the signal handle — the entry association
      // is preserved regardless of exit_mode. Only the trigger source changes.
      const _exitActions = ['sl', 'tp', 'exit_long', 'exit_short', 'time_exit'];
      if ((type === 'signal' || type === 'position_trigger') && _exitActions.includes(dataArg?.action)) {
          const bAction     = dataArg?.action;
          const positionType = (dataArg?.type || '').toUpperCase();
          // Infer direction: explicit type field takes priority; action verb is the fallback.
          const isRiskLong  = positionType === 'LONG'  || positionType === 'LONG RISK'
                              || (!positionType && bAction === 'exit_long');
          const isRiskShort = positionType === 'SHORT' || positionType === 'SHORT RISK'
                              || (!positionType && bAction === 'exit_short');
          const edgeColor   = bAction === 'sl' ? '#f43f5e' : bAction === 'tp' ? '#10b981' : '#6366f1';
          const exitStage   = dataArg?.stage || 1;

          // Find the entry whose stage matches this exit's stage.
          const allEntries   = findAllAssociatedEntryNodes(nodes, null, isRiskLong, isRiskShort);
          const matchedEntry = allEntries.find(en => (en.data?.stage || 1) === exitStage);

          if (matchedEntry) {
              newNode.data.inputs = [{
                  type:   'link',
                  nodeId: matchedEntry.id,
                  field:  'value',
                  id:     'input_0'
              }];
              edgesToAdd.push({
                  id:           `e_${matchedEntry.id}_${newNode.id}_input_0`,
                  source:       matchedEntry.id,
                  target:       newNode.id,
                  targetHandle: 'input_0',
                  sourceHandle: 'value',
                  animated:     true,
                  style: { strokeWidth: 2, stroke: edgeColor, strokeDasharray: '5,5' }
              });
          }
      }

      // C. Visual Output Wiring (Node -> Chart)
      let targetChart = null;
      let shouldCreateSubChart = false;

      if (isChartVisual) {
          targetChart = mainChartNode;
      } else if (type === 'indicator') {
          // Priority: Actual Data -> Visual Settings -> Default
          const isOverlay = dataArg?.is_overlay || dataArg?.overlay || 
                           dataArg?.visual_settings?.is_overlay || 
                           (nodeConfig.isOverlay) || // Use Config Default
                           (['custom', 'marker'].includes(dataArg?.visual_settings?.type));
          
          if (isOverlay) {
              targetChart = mainChartNode;
          } else {
              if (subChartNode) {
                  targetChart = subChartNode;
              } else {
                  shouldCreateSubChart = true;
              }
          }
      }

      if (targetChart) {
          connectToChart(newNode.id, targetChart.id);
      } else if (shouldCreateSubChart && options.autoCreateChartPane !== false) {
          const subChartId = `chart_output_${Math.random().toString(36).substr(2, 9)}`;
          const subChartPos = { x: finalPosition.x + 200, y: finalPosition.y + 100 };
          const newSubChart = {
              id: subChartId,
              type: 'chart_output',
              position: subChartPos,
              data: { panel: 'sub', label: 'Ind. Panel' }
          };
          setNodes(nds => nds.concat(newSubChart));
          connectToChart(newNode.id, subChartId);
      }

      if (isChartVisual && dataSourceNode) {
          newNode.data.sourceId = dataSourceNode.id;
          newNode.data.input = dataSourceNode.id;
           edgesToAdd.push({
                id: `e_${dataSourceNode.id}_${newNode.id}`,
                source: dataSourceNode.id,
                target: newNode.id,
                animated: true,
                style: { ...GRAPH_EDGE_STYLES.DATA_TO_VISUAL }
           });
      }

      // 6. State Updates (Perform Side Effects)
      //
      // C-1 Reconciliation: When a new entry node is created, wire it to the appropriate
      // exit nodes based on their mode:
      //
      //   SINGLE exits (execution_strategy !== 'multi_stage'):
      //     Wire to ALL direction-matching entries regardless of stage.
      //     These exits become SHARED (badge + multi-handle spread) as entries accumulate.
      //     Each new entry gets the next free input_N slot so handles don't collide.
      //
      //   STAGE N exits (execution_strategy === 'multi_stage' with stage N):
      //     Wire ONLY to the entry whose stage equals N. One-to-one, always input_0.
      //
      // This preserves the user expectation: "single exits auto-wire to EVERY entry,
      // stage-specific exits only wire to their own stage's entry."
      const exitNodePatches = new Map(); // exitNodeId → { newInput, newEdge }

      if (dataArg && ['enter_long', 'enter_short', 'buy', 'sell'].includes(dataArg.action)) {
          const isEntryLong  = dataArg.action === 'enter_long' || dataArg.action === 'buy';
          const isEntryShort = dataArg.action === 'enter_short' || dataArg.action === 'sell';
          const entryStage   = dataArg?.stage || 1;
          // All exit node types that should be auto-patched when a new entry is created.
          const _allExitActions = ['sl', 'tp', 'exit_long', 'exit_short', 'time_exit'];

          nodes.forEach(n => {
              if (n.type !== 'signal' && n.type !== 'position_trigger') return;
              if (!_allExitActions.includes(n.data?.action)) return;

              const nAction         = n.data?.action;
              const nType           = (n.data?.type || '').toUpperCase();
              const isMultiStageExit = n.data?.execution_strategy === 'multi_stage';

              // Infer exit direction from type field; fallback to action verb.
              const exitIsLong  = nType === 'LONG'  || nType === 'LONG RISK'
                                  || (!nType && nAction === 'exit_long');
              const exitIsShort = nType === 'SHORT' || nType === 'SHORT RISK'
                                  || (!nType && nAction === 'exit_short');
              // time_exit with no type matches both directions —
              // only patch when entry direction agrees.
              const directionMatch =
                  (isEntryLong  && exitIsLong) ||
                  (isEntryShort && exitIsShort) ||
                  (nAction === 'time_exit' && !exitIsLong && !exitIsShort); // untyped time_exit
              if (!directionMatch) return;

              // STAGE N exits: enforce strict stage equality (one-to-one).
              // SINGLE exits: skip the stage guard — they wire to every matching entry (SHARED).
              const exitStage = n.data?.stage || 1;
              if (isMultiStageExit && exitStage !== entryStage) return;

              // Already linked to this exact entry? Skip.
              const existingInputs = Array.isArray(n.data.inputs) ? n.data.inputs : [];
              if (existingInputs.some(inp => inp.nodeId === newNode.id)) return;

              // For untyped time_exit: prevent signal-slot collision when both enter_long
              // and enter_short exist at the same stage (directionMatch passes for both).
              // time_exit should only ever have ONE signal input.
              if (nAction === 'time_exit' && existingInputs.some(inp => inp.id === 'input_0')) return;

              const _eColor = nAction === 'sl' ? '#f43f5e'
                             : nAction === 'tp' ? '#10b981'
                             : '#6366f1';

              // Determine the target handle slot:
              //   - STAGE N exits → always input_0 (dedicated one-to-one slot)
              //   - SINGLE exits  → next available input_N (grows as SHARED accumulates entries)
              let targetInputId;
              if (isMultiStageExit) {
                  targetInputId = 'input_0';
              } else {
                  const nextIdx = existingInputs.reduce((max, inp) => {
                      const num = parseInt((inp.id || '').replace('input_', ''), 10);
                      return isNaN(num) ? max : Math.max(max, num + 1);
                  }, existingInputs.length);
                  targetInputId = `input_${nextIdx}`;
              }

              exitNodePatches.set(n.id, {
                  newInput: { type: 'link', nodeId: newNode.id, field: 'value', id: targetInputId },
                  newEdge: {
                      id:           `e_${newNode.id}_${n.id}_${targetInputId}`,
                      source:       newNode.id,
                      target:       n.id,
                      targetHandle: targetInputId,
                      sourceHandle: 'value',
                      animated:     true,
                      style: { strokeWidth: 2, stroke: _eColor, strokeDasharray: '5,5' }
                  }
              });
          });
      }

      // Apply node additions and patches in one setNodes call.
      setNodes(nds => {
          let nextNodes = nds.concat(newNode);
          if (exitNodePatches.size > 0) {
              nextNodes = nextNodes.map(n => {
                  const patch = exitNodePatches.get(n.id);
                  if (!patch) return n;
                  const existingInputs = Array.isArray(n.data.inputs) ? n.data.inputs : [];
                  return { ...n, data: { ...n.data, inputs: [...existingInputs, patch.newInput] } };
              });
          }
          return nextNodes;
      });

      // Emit all edges (auto-wiring + exit-patch) in one setEdges call.
      const patchEdges = exitNodePatches.size > 0
          ? Array.from(exitNodePatches.values()).map(p => p.newEdge)
          : [];
      const allEdgesToAdd = [...edgesToAdd, ...patchEdges];
      if (allEdgesToAdd.length > 0) {
          setEdges(eds => eds.concat(allEdgesToAdd));
      }

      return {
          success: true,
          node: newNode,
          createdEdges: allEdgesToAdd,
          autoUpgraded,
          autoUpgradedLabels
      };

  }, [nodes, setNodes, setEdges]);

  /**
   * handleShowSlope
   * Auto-creates and wires a Dee Trend Slope node to an existing node.
   */
  const handleShowSlope = useCallback((sourceNodeId) => {
      const sourceNode = nodes.find(n => n.id === sourceNodeId);
      if (!sourceNode) return;

      const existingSlope = nodes.find(n => 
          n.type === 'indicator' && 
          n.data?.indicator === 'dee_trend_slope' && 
          n.data?.sourceId === sourceNodeId
      );

      if (existingSlope) return;

      const pos = { x: sourceNode.position.x + 350, y: sourceNode.position.y };
      
      // Use centralized default
      const config = getNodeConfig('dee_trend_slope');
      
      const data = {
          ...config.defaultData, // Spread default params and visual settings
          label: `Slope (${sourceNode.data?.indicator || sourceNode.data?.label || 'Ind'})`,
          sourceId: sourceNodeId,
          timeframe: sourceNode.data?.timeframe || '5m'
      };

      createNode('indicator', pos, data);
  }, [nodes, createNode]);

  return { createNode, handleShowSlope };
};
